/**
 * NaamDarbar — Navbar (Theme-aware)
 *
 * Orchestrates the sticky header: logo, desktop nav + profile dropdown,
 * mobile quick links + slide-down menu. Only the layout for the current
 * viewport is mounted (via useIsMobile) so mobile devices don't pay for
 * desktop markup and vice versa.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../context/ThemeContext';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import ThemeToggle from '../../ui/ThemeToggle';
import DesktopNav from './DesktopNav';
import MobileMenu from './MobileMenu';
import ProfileDropdown from './ProfileDropdown';

function Divider({ isSukhasan }: { isSukhasan: boolean }) {
  return <div className={cn('h-5 w-px', isSukhasan ? 'bg-white/[0.08]' : 'bg-line')} />;
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { theme } = useTheme();
  const isSukhasan = theme === 'sukhasan';
  const isMobile = useIsMobile();

  // Close the mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useBodyScrollLock(mobileOpen);

  return (
    <>
      <header
        className="sticky top-0 z-50 backdrop-blur-xl transition-colors duration-300"
        // Glass chrome matching the app's tab bar: near-opaque so the mobile
        // menu never shows content through, frosted on capable browsers
        style={{ backgroundColor: 'var(--tabbar-glass-bg)' }}
      >
        {/* Subtle bottom border line */}
        <div className="absolute inset-x-0 bottom-0 h-px" style={{ backgroundColor: 'var(--glass-border)' }} />

        <div className="container-page flex h-14 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 group">
            <img
              src={isSukhasan ? '/Dark_theme_brand_text.svg' : '/White_theme_brand_text.svg'}
              alt="Naam Darbar"
              className="h-[22px] w-auto"
            />
          </Link>

          {isMobile ? (
            /* Mobile: hamburger */
            <div className="flex items-center gap-1.5">
              <button
                className={cn(
                  'rounded-lg p-1.5 transition-colors',
                  isSukhasan ? 'text-slate-400 hover:bg-white/[0.06]' : 'text-tx-muted hover:bg-elevated',
                )}
                onClick={() => setMobileOpen((v) => !v)}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          ) : (
            /* Desktop: centered nav + right action cluster */
            <>
              <DesktopNav />
              <div className="flex items-center gap-3 flex-shrink-0">
                <ThemeToggle />
                <Divider isSukhasan={isSukhasan} />
                <ProfileDropdown />
              </div>
            </>
          )}
        </div>

        {isMobile && (
          <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
        )}
      </header>
    </>
  );
}
